import { useCallback, useEffect, useState } from "react";

import { Badge, BadgeProps } from "@mantine/core";
import _ from "lodash";

import { CheckboxList, packSx } from "components/mantine-extended";
import { SectionToggle } from "components/SectionToggle";
import { useSearchRouteInfo } from "hooks/routing";

interface CollapsibleFilterProps {
  label: string;
  queryParamName:
    | "location"
    | "region"
    | "industry"
    | "stage"
    | "title"
    | "companyName"
    | "headcount"
    | "yoe"
    | "tier";
  getInitiallySelected?: () => string[];
  onChange?: (selected: string[]) => void;
  data: string[] | { label: string; value: string }[];
  maxNumberListItemsToRender?: number;
  onSearchChange?: (search: string) => void;
  fetchPage?: (page: number) => Promise<void>;
  showSearch?: boolean;
  initiallyOpen?: boolean;
  selectionSubtext?: string;
}

export function CollapsibleFilter({
  maxNumberListItemsToRender = 100,
  ...props
}: CollapsibleFilterProps): JSX.Element {
  const { query, queryString } = useSearchRouteInfo();
  const [fetchPageNumber, setFetchPageNumber] = useState(0);
  const [fetchPageLoading, setFetchPageLoading] = useState(false);

  const getInitiallySelected = useCallback(
    function (): string[] {
      if (props.getInitiallySelected) {
        return props.getInitiallySelected();
      }

      return _.get(query, props.queryParamName) || [];
    },
    [props.queryParamName, query, props.getInitiallySelected],
  );

  const [selected, setSelected] = useState(getInitiallySelected());

  useEffect(() => {
    setSelected(getInitiallySelected());
  }, [getInitiallySelected, queryString]);

  function onScrollToBottom() {
    if (props.fetchPage && !fetchPageLoading) {
      const nextPage = fetchPageNumber + 1;
      setFetchPageLoading(true);
      props.fetchPage(nextPage).then(() => {
        setFetchPageNumber(nextPage);
        setFetchPageLoading(false);
      });
    }
  }

  return (
    <SectionToggle
      title={props.label}
      initiallyOpen={props.initiallyOpen ?? selected.length > 0}
      rightSection={
        selected.length > 0 && (
          <FiltersSelectedBadge>{`${selected.length} Selected`}</FiltersSelectedBadge>
        )
      }
    >
      <CheckboxList
        data={props.data.map(toLabelValue)}
        onChange={(sel) => {
          setSelected(sel);
          if (props.onChange) {
            props.onChange(sel);
          }
        }}
        selectionSubtext={props.selectionSubtext}
        onSearchChange={props.onSearchChange}
        maxNumberToRender={maxNumberListItemsToRender}
        selected={selected}
        onScrollToBottom={onScrollToBottom}
        showSearch={props.showSearch}
      />
    </SectionToggle>
  );
}

export function toLabelValue<T extends string | { label: string; value: string }>(
  val: T,
): { label: string; value: string } {
  if (typeof val === "string") {
    return { label: val, value: val };
  } else {
    return val;
  }
}

export function FiltersSelectedBadge({ sx, ...props }: BadgeProps) {
  return (
    <Badge
      size={"xs"}
      variant={"filled"}
      radius={50}
      sx={[
        {
          textTransform: "none",
        },
        ...packSx(sx),
      ]}
      {...props}
    />
  );
}
